import { InfoIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetPointCentralClientMapping } from '@/api/keyless/point-central/get-point-central-client-mapping'
import { useUnauthorizePointCentral } from '@/api/keyless/point-central/unauthorize-point-central'
import { useIsPointCentralBuildingConnected } from '@/api/property-keyless/point-central/use-is-point-central-building-connected'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Flex } from '@/components/ui/flex'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { popupFactory, toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils/error'

import * as S from '../keyless-access.styled'
import { KeylessBuildingNotConnected } from '../keyless-access-building-not-connected'
import { KeylessTabSkeleton } from '../keyless-access-tab-skeleton'
import { PointCentralTabProps } from './point-central-tab.types'

export const PointCentralTab = ({
  clientId,
  buildingId,
  onLoadingChange,
}: PointCentralTabProps) => {
  const { t } = useTranslation(['clients'])

  const {
    data: mapping,
    isFetching,
    isSuccess: isMappingSuccess,
    refetch,
  } = useGetPointCentralClientMapping({ buildingId, clientId })
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsPointCentralBuildingConnected(buildingId)
  const { mutateAsync: unauthorize, isPending: isDisconnecting } = useUnauthorizePointCentral()

  const isTabLoading = isFetching || isLoadingBuildingConnection

  useEffect(() => {
    onLoadingChange?.(isTabLoading)
    return () => onLoadingChange?.(false)
  }, [isTabLoading, onLoadingChange])

  if (isTabLoading) return <KeylessTabSkeleton rowCount={1} />

  if (!isBuildingConnected) {
    return (
      <KeylessBuildingNotConnected
        title={t('clients:keylessAccess.pointCentral.buildingNotConnected.title')}
        message={t('clients:keylessAccess.pointCentral.buildingNotConnected.message')}
      />
    )
  }

  // Note: gated on `isSuccess`, not just the raw response - React Query keeps the last
  // successful value in `data` even after a later background refetch fails, which would
  // otherwise leave this resident looking connected once a stale value was ever fetched.
  const isConnected = isMappingSuccess && Boolean(mapping?.isSynced)

  const handleDisconnect = async () => {
    try {
      await unauthorize({ buildingId, clientId })
      toastFactory.success(t('clients:keylessAccess.pointCentral.toasts.disconnectSuccess'))
      void refetch()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleOpenDisconnectConfirm = () => {
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.pointCentral.disconnect.confirmBody'),
      confirmText: t('clients:keylessAccess.pointCentral.disconnect.confirmText'),
      onConfirm: handleDisconnect,
      title: t('clients:keylessAccess.pointCentral.disconnect.action'),
      variant: 'danger',
    })
  }

  if (!isConnected) {
    return (
      <S.TabContent>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.pointCentral.status.label')}
            </Typography>
          </Flex>
          <S.FieldValue>
            <Typography size="text-medium" color="black500" as="span">
              {t('clients:keylessAccess.pointCentral.status.notMapped')}
            </Typography>
          </S.FieldValue>
        </S.FieldRow>
      </S.TabContent>
    )
  }

  return (
    <S.TabContent>
      <Flex gap={8} justify="flex-end" padding="12 16">
        <Button
          size="small"
          use="secondary"
          loading={isDisconnecting}
          onClick={handleOpenDisconnectConfirm}
        >
          {t('clients:keylessAccess.pointCentral.disconnect.action')}
        </Button>
        <Tooltip triggerIcon={InfoIcon}>
          {t('clients:keylessAccess.pointCentral.disconnect.tooltip')}
        </Tooltip>
      </Flex>
    </S.TabContent>
  )
}
